import * as React from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChoiceList, ChoiceRow } from '@/components/ui/choice-card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useGhlReview, useResolveGhlReview } from '@/hooks/useGoHighLevel'
import { GHL_MAX_LINKS_PER_EVENT } from '@lib/constants/gohighlevel'
import type { GhlCoupleDto, GhlReviewItemDto } from '@lib/dto/gohighlevel.dto'
import { GhlLeadPicker, coupleSummary } from './GhlLeadPicker'

const pct = (p: number | null): string => (p === null ? '' : `${String(Math.round(p * 100))}%`)

function eventDay(day: string | null): string {
  if (day === null) return 'no date'
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function refsOf(couple: GhlCoupleDto) {
  return couple.opportunities.slice(0, GHL_MAX_LINKS_PER_EVENT).map((o) => ({
    connectionId: o.connectionId,
    opportunityId: o.opportunityId,
    contactId: o.contactId,
    email: couple.email,
  }))
}

/**
 * What the backfill couldn't link on its own (docs/gohighlevel.spec.md): Clef
 * wasn't sure, the clients' emails matched more than one lead, or the lead is
 * already on another event. For organization admins to settle.
 */
export function GhlReviewList({ venueId }: { venueId: string }) {
  const review = useGhlReview(venueId, true)
  if (review.isPending) return <p className="m-0 text-sm text-ink-muted">Reading leads from GoHighLevel…</p>
  if (review.isError) return <ErrorState message={requestErrorMessage(review.error)} onRetry={() => void review.refetch()} />
  if (review.data.items.length === 0) {
    return <EmptyState icon={CheckCircle2} title="Nothing to review" />
  }
  const { items, total } = review.data
  return (
    <div className="space-y-3">
      {total > items.length ? (
        <p className="m-0 text-sm text-ink-muted">
          Showing the oldest {items.length} of {total}. Settle these and the next ones appear.
        </p>
      ) : null}
      {items.map((item) => (
        <ReviewCard key={item.id} venueId={venueId} item={item} />
      ))}
    </div>
  )
}

function reasonText(item: GhlReviewItemDto): React.ReactNode {
  if (item.reason === 'clef_unsure') return `Clef wasn’t sure (${pct(item.suggestedProbability)}).`
  if (item.reason === 'email_ambiguous') return 'This event’s clients match more than one lead.'
  if (item.takenBy === null) return 'This event’s client was linked to another event.'
  return (
    <>
      This event’s client is already linked to{' '}
      <Link to={`/events/${item.takenBy.eventId}`} className="font-medium text-ink underline underline-offset-2">
        {item.takenBy.eventName}
      </Link>{' '}
      ({eventDay(item.takenBy.eventDate)}).
    </>
  )
}

function CoupleTitle({ item, couple, index }: { item: GhlReviewItemDto; couple: GhlCoupleDto; index: number }) {
  return (
    <span className="flex items-center gap-2">
      <span className="truncate">{couple.name}</span>
      {item.suggestedIndex === index ? <Badge tone="info">Clef’s pick · {pct(item.suggestedProbability)}</Badge> : null}
    </span>
  )
}

interface CandidateListProps {
  item: GhlReviewItemDto
  /** The lead is on another event, so these are shown, not offered. */
  taken: boolean
  selected: number | null
  onSelect: (index: number) => void
}

/** The leads that might be this event's: a choice of one, or — when the lead is taken — just the list. */
function CandidateList({ item, taken, selected, onSelect }: CandidateListProps) {
  if (taken) {
    return (
      <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-lg border border-line p-0">
        {item.candidates.map((couple, i) => (
          <li key={couple.key} className="px-3 py-2.5">
            <span className="block text-sm font-medium text-ink">
              <CoupleTitle item={item} couple={couple} index={i} />
            </span>
            <span className="mt-0.5 block truncate text-small text-ink-muted">{coupleSummary(couple)}</span>
          </li>
        ))}
      </ul>
    )
  }
  return (
    <ChoiceList label={`GoHighLevel leads for ${item.eventName}`}>
      {item.candidates.map((couple, i) => (
        <ChoiceRow
          key={couple.key}
          type="radio"
          name={`review-${item.id}`}
          checked={selected === i}
          onSelect={() => onSelect(i)}
          title={<CoupleTitle item={item} couple={couple} index={i} />}
          description={<span className="block truncate">{coupleSummary(couple)}</span>}
        />
      ))}
    </ChoiceList>
  )
}

function ReviewCard({ venueId, item }: { venueId: string; item: GhlReviewItemDto }) {
  const resolve = useResolveGhlReview(venueId)
  const [selected, setSelected] = React.useState<number | null>(item.suggestedIndex)
  const [choosing, setChoosing] = React.useState(false)
  const chosen = selected === null ? undefined : item.candidates[selected]
  // A taken lead is on another event: linking it here would only be refused.
  const taken = item.reason === 'email_taken'

  return (
    <Card asChild className="space-y-3 p-4">
      <section>
        <div>
          <Link to={`/events/${item.eventId}`} className="text-sm font-medium text-ink underline-offset-2 hover:underline">
            {item.eventName}
          </Link>
          <p className="m-0 text-xs text-ink-muted">{eventDay(item.eventDate)}</p>
        </div>
        <p className="m-0 text-sm text-ink-muted">{reasonText(item)}</p>

        {taken ? (
          <p className="m-0 text-sm text-ink-muted">
            To link this event to that lead, unlink it from the other event first. Or use Choose another… for a different lead.
          </p>
        ) : null}

        {item.candidates.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">These leads can’t be read from GoHighLevel right now.</p>
        ) : (
          <CandidateList item={item} taken={taken} selected={selected} onSelect={setSelected} />
        )}

        {resolve.error ? <FormError>{requestErrorMessage(resolve.error)}</FormError> : null}

        <div className="flex flex-wrap gap-2">
          {taken ? null : (
            <Button
              size="sm"
              disabled={chosen === undefined || resolve.isPending}
              onClick={() => {
                if (chosen !== undefined) resolve.mutate({ itemId: item.id, input: { opportunities: refsOf(chosen) } })
              }}
            >
              Link to selected
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={resolve.isPending} onClick={() => setChoosing(true)}>
            Choose another…
          </Button>
          <Button size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => resolve.mutate({ itemId: item.id, input: null })}>
            No GHL match
          </Button>
        </div>

        <Dialog open={choosing} onOpenChange={setChoosing}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Choose the lead for {item.eventName}</DialogTitle>
              <DialogDescription>Any couple at this venue without an event.</DialogDescription>
            </DialogHeader>
            <GhlLeadPicker
              venueId={venueId}
              value={null}
              onPick={(couple) =>
                resolve.mutate(
                  { itemId: item.id, input: { opportunities: refsOf(couple) } },
                  { onSuccess: () => setChoosing(false) },
                )
              }
              onClear={() => undefined}
              idPrefix={`review-${item.id}`}
              canChooseStages
            />
          </DialogContent>
        </Dialog>
      </section>
    </Card>
  )
}
